import React from "react";
import {Badge, Tooltip} from "@mui/material";
import {
    ArchitectureOutlined,
    ChatBubbleOutlineOutlined,
    ImageOutlined
} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {RequestOverviewRowDTO} from "@/api/data-contracts";
import {RequestI18N} from "@/translations";
import {TOOLTIP_DELAY_MS} from "@/constants";

export interface RequestInfoCellProps {
    request: RequestOverviewRowDTO;
}

/**
 * Renders the "info" column cell for the request overview table,
 * showing icons for print images, blueprints, and unread messages.
 */
export const RequestInfoCell: React.FC<RequestInfoCellProps> = ({request}) => {
    const {t} = useTranslation();
    const unreadCount = Math.max(0, request.oneToOneUnreadCount ?? 0);
    return (
        <div style={{display: "flex", gap: "0.5rem", alignItems: "center"}}>
            {request.hasPrintImages && (
                <Tooltip
                    title={t(RequestI18N.details.field.hasPrintImages)}
                    enterDelay={TOOLTIP_DELAY_MS}
                    enterNextDelay={TOOLTIP_DELAY_MS}
                    arrow
                >
                    <span>
                        <ImageOutlined color="action" fontSize="small"/>
                    </span>
                </Tooltip>
            )}
            {request.hasBluePrints && (
                <Tooltip
                    title={t(RequestI18N.details.field.hasBlueprints)}
                    enterDelay={TOOLTIP_DELAY_MS}
                    enterNextDelay={TOOLTIP_DELAY_MS}
                    arrow
                >
                    <span>
                        <ArchitectureOutlined color="action" fontSize="small"/>
                    </span>
                </Tooltip>
            )}
            {unreadCount > 0 && (
                <Tooltip
                    title={t(RequestI18N.details.tooltip.unreadMessages, {count: unreadCount})}
                    enterDelay={TOOLTIP_DELAY_MS}
                    enterNextDelay={TOOLTIP_DELAY_MS}
                    arrow
                >
                    <span>
                        <Badge badgeContent={unreadCount} color="error">
                            <ChatBubbleOutlineOutlined fontSize="small"/>
                        </Badge>
                    </span>
                </Tooltip>
            )}
        </div>
    );
};
